/**
 * @file NoticeRiskDialog.tsx
 * @description Окно о риске режима «Уведомление» (phase19.16, партия Р3г,
 * v0.19.62; SB133 «выбор «Уведомление» — окно с описанием риска и галочкой
 * «отвечаю сам» (запись в журнале действий)»). Тексты — текущая редакция
 * платформы (`consentPlatform.noticeRisk.texts`; пока сервер её не отдаёт —
 * встроенная `BUILT_IN_NOTICE_RISK_TEXTS`); номер этой же редакции уходит в
 * `noticeAck` (`useCountersDraft`) — сотрудник подтверждает ровно то, что
 * видел. Текст на сервере успел смениться — 422, часть перечитывается.
 *
 * «Включить» неактивна, пока галочка не стоит: без неё режим не меняется и
 * ничего не сохраняется. Каждое открытие — галочка снята. На телефоне окно
 * во весь экран (правило диалогов < sm). Подписи кнопок — общая константа
 * `NOTICE_RISK_BUTTON_LABELS` (та же у образца окна в кабинете платформы).
 */

import type { ConsentNoticeRiskTexts } from "@chaiberi/shared";
import { useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { CheckRow } from "../CheckRow";
import { NOTICE_RISK_BUTTON_LABELS } from "../../../lib/site-view/notice-risk-labels";

interface Props {
  open: boolean;
  /** Тексты показанной редакции окна. */
  texts: ConsentNoticeRiskTexts;
  onCancel: () => void;
  /** Галочка поставлена и нажата «Включить». */
  onConfirm: () => void;
}

export function NoticeRiskDialog({ open, texts, onCancel, onConfirm }: Props) {
  const [ack, setAck] = useState(false);
  // Новое открытие — галочка снята (прошлое согласие не переносится).
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) setAck(false);
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onCancel() : undefined)}>
      {/* Ширина — общая у окон (sm:max-w-lg): в 448 px две длинные кнопки не помещались, левая уезжала к краю (QA D3). */}
      <DialogContent fullScreen>
        <DialogHeader>
          <DialogTitle>{texts.title}</DialogTitle>
          <DialogDescription>{texts.body}</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl bg-warning/12 px-3 py-1 [&>label]:items-start [&>label]:py-3 [&>label>[data-slot=checkbox]]:mt-[3px]">
          <CheckRow checked={ack} onChange={setAck}>
            {texts.acknowledge}
          </CheckRow>
        </div>
        <DialogFooter className="sm:flex-wrap">
          <Button variant="outline" onClick={onCancel}>
            {NOTICE_RISK_BUTTON_LABELS.keepConsent}
          </Button>
          <Button disabled={!ack} onClick={onConfirm}>
            {NOTICE_RISK_BUTTON_LABELS.enableNotice}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
